import { Status, StatusDropdown } from "components";
import UserWithAvatar from "components/Atoms/UserWithAvatar";
import CultureLabel from "components/Culture/CultureLabel";
import { FormSection, RenderEditOptions } from "components/MainSectionTemplate/types";
import UserModalPicker from "components/Picker/UserPicker/UserModalPicker";
import { BookkeepingSmallDetails } from "components/SmallDetails";
import CountryDropdown from "components/dropdowns/reactselect/CountryDropdown";
import CulturesInterfaceDropdown from "components/dropdowns/reactselect/CulturesInterfaceDropdown";
import React from "react";
import CulturesSelect from "./CulturesSelect";
import { getUpdateInstruction, WebformFormState } from "./model";
import BookkeepingPaginatedDropdown from "components/dropdowns/paginated/BookkeepingPaginatedDropdown";

export const defaultValuesSection = (
  webformFormState: WebformFormState,
): FormSection => ({
  key: "defaultValues",
  title: RESX.PersonalSettings.DefaultValues,
  type: "collapsable",
  fields: [{
    key: "defaultBookkeeping",
    label: RESX.Bookkeeping.DefaultBookkeeping,
    render: () => <BookkeepingSmallDetails bookkeeping={webformFormState.defaultBookkeeping} />,
    inPageEdit: {
      render: ({ value, onChange, cancel }: RenderEditOptions<server.dto.BookkeepingSmall>) => <BookkeepingPaginatedDropdown
        value={value}
        onChange={(bk) => {
          onChange(bk, true);
        }}
        onEsc={() => cancel()}
        config={{
          focusOnMount: true,
          openMenuOnFocus: true,
        }}
      />,
      fullWidth: true,
      type: "custom",
      initialValue: webformFormState.defaultBookkeeping,
      submitChange: async (bookkeeping: server.dto.BookkeepingSmall) => window.CoreApi.WebformConfiguration.UpdateDetails({
        ...getUpdateInstruction(webformFormState),
        defaultBookkeepingId: bookkeeping.bookkeepingId,
      })
    },
  }, {
    key: "defaultStatus",
    label: RESX.Status.DefaultStatus,
    render: () => <Status status={webformFormState.defaultStatus} />,
    inPageEdit: {
      render: ({ value, onChange }) => (
        <StatusDropdown
          value={value}
          onChange={onChange}
          mainArea="show"
          clientId={webformFormState.clientId}
          options={undefined}
          config={{
            focusOnMount: true,
            openMenuOnFocus: true
          }}
        />
      ),
      type: "custom",
      initialValue: webformFormState.defaultStatus,
      fullWidth: true,
      submitChange: async (status: server.dto.StatusSmall) => window.CoreApi.WebformConfiguration.UpdateDetails({
        ...getUpdateInstruction(webformFormState),
        defaultStatusId: status.statusId,
      })
    }
  }, {
    key: "defaultCountry",
    label: RESX.PersonalSettings.PreselectedCountry,
    render: () => webformFormState.defaultCountry.name,
    inPageEdit: {
      render: ({ value, onChange }) => (
        <CountryDropdown
          value={value}
          onChange={onChange}
          config={{
            focusOnMount: true,
            openMenuOnFocus: true
          }}
        />
      ),
      type: "custom",
      initialValue: webformFormState.defaultCountry,
      fullWidth: true,
      submitChange: async (country: server.dto.Country) => window.CoreApi.WebformConfiguration.UpdateDetails(
        getUpdateInstruction({ ...webformFormState, defaultCountry: country })
      )
    }
  }, {
    key: "defaultUser",
    label: RESX.Webform.DefaultUser,
    render: () => <UserWithAvatar user={webformFormState.defaultUser} />,
    inPageEdit: {
      hoverEdit: true,
      render: ({ onChange, cancel, value }) => (
        <div className="main-section-template-fields-padding">
          <UserModalPicker
            inPageEditing={{ onChange, cancel, hasValue: Boolean(value) }}
            canCreateNew={false}
          />
        </div>
      ),
      type: "custom",
      initialValue: webformFormState.defaultUser,
      submitChange: async (user: server.dto.UserSmall) => window.CoreApi.WebformConfiguration.UpdateDetails(
        getUpdateInstruction({ ...webformFormState, defaultUser: user })
      )
    }
  }, {
    key: "defaultCulture",
    label: RESX.Language.DefaultLanguage,
    render: () => <CultureLabel culture={webformFormState.defaultCulture} />,
    inPageEdit: {
      render: ({ value, onChange }) => (
        <CulturesInterfaceDropdown
          value={value}
          onChange={onChange}
          config={{
            focusOnMount: true,
            openMenuOnFocus: true
          }}
        />
      ),
      type: "custom",
      initialValue: webformFormState.defaultCulture,
      fullWidth: true,
      submitChange: async (culture: server.dto.Culture) => window.CoreApi.WebformConfiguration.UpdateDetails(
        getUpdateInstruction({ ...webformFormState, defaultCulture: culture })
      )
    }
  }, {
    key: "availableCultures",
    label: RESX.Webform.DisplayFlags,
    isEmpty: webformFormState.availableCultures.length === 0,
    render: () => <>
      {webformFormState.availableCultures.map(culture => <div key={culture.value}>
        <CultureLabel culture={culture} />
      </div>)}
    </>,
    inPageEdit: {
      render: ({ value, onChange }) => <div className="main-section-template-fields-padding">
        <CulturesSelect
          value={value}
          onChange={onChange}
        />
      </div>,
      fullWidth: true,
      type: "confirmOrCancelButton",
      initialValue: webformFormState.availableCultures,
      submitChange: (availableCultures: server.dto.Culture[]) => window.CoreApi.WebformConfiguration.UpdateDetails(
        getUpdateInstruction({ ...webformFormState, availableCultures }),
      ),
    }
  }]
});